import React, { useRef } from 'react';
import { CalendarProps } from '../types';
import { useCalendarState } from '../hooks/useCalendarState';
import { useCalendarData } from '../hooks/useCalendarData';
import { useHolidays } from '../hooks/useHolidays';
import { useCalendarCellWidth } from '../../../hooks/useCalendarCellWidth';
import { CalendarHeader } from '../shared/CalendarHeader';
import { WeekdayHeader } from '../shared/WeekdayHeader';
import { DesktopDateCell } from './DesktopDateCell';
import { getCalendarDateInfo, isToday, isCurrentMonth, isSelected, toCalendarDateKey } from '../shared/utils';

const CalendarDesktop: React.FC<CalendarProps & { userId?: string | null }> = ({
  onTodoClick,
  onDateClick,
  onDateDoubleClick,
  selectedDate: externalSelectedDate,
  selectedGroupIds = new Set<string>(),
  personalGroupId,
  dataVersion,
  todosForCalendar,
  onTodosInvalidate,
  onTodoUpdated,
  onOptimisticToggle,
  skipTodosReloadUntilRef,
  getCurrentTodosForToggle,
  currentTodosForToggle,
  userId,
}) => {
  const {
    state,
    setCurrentDate,
    setMonthTodos,
    setHolidays,
    selectedDate,
  } = useCalendarState(externalSelectedDate, userId);

  const { loadMonthTodos } = useCalendarData(
    state.currentDate,
    selectedGroupIds,
    setMonthTodos,
    personalGroupId,
    dataVersion,
    todosForCalendar,
    skipTodosReloadUntilRef
  );

  useHolidays(state.currentDate, setHolidays);

  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const isCompactCell = useCalendarCellWidth(calendarContainerRef);

  const dateInfo = getCalendarDateInfo(state.currentDate, false);

  // 切换到上个月
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(dateInfo.year, dateInfo.month - 1, 1));
  };

  // 切换到下个月
  const goToNextMonth = () => {
    setCurrentDate(new Date(dateInfo.year, dateInfo.month + 1, 1));
  };

  // 切换到今天
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    handleDateClick(today);
  };

  // 选择日期（单击）
  const handleDateClick = (date: Date) => {
    onDateClick?.(date);
  };

  // 双击日期处理
  const handleDateDoubleClick = (date: Date) => {
    onDateDoubleClick?.(date);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-col flex-1 min-h-0 py-2 pb-4">
        <CalendarHeader
          year={dateInfo.year}
          month={dateInfo.month}
          onPreviousMonth={goToPreviousMonth}
          onNextMonth={goToNextMonth}
          onToday={goToToday}
        />

        <WeekdayHeader />

        {/* 日历网格：统一整体，仅 1px 缝隙分隔 */}
        <div
          ref={calendarContainerRef}
          className="grid grid-cols-7 gap-px flex-1 min-h-0 rounded-b-lg overflow-hidden"
          style={{ gridTemplateRows: 'repeat(6, minmax(0, 1fr))' }}
        >
        {dateInfo.calendarDays.map((date, index) => {
          if (!date) {
            return <div key={index} />;
          }

          const dateKey = toCalendarDateKey(date);
          const dayTodos = state.monthTodos.get(dateKey) || [];
          const isCurrentMonthDay = isCurrentMonth(date, dateInfo.month);
          const isTodayDay = isToday(date);
          const isSelectedDay = isSelected(date, selectedDate);

          return (
            <DesktopDateCell
              key={index}
              date={date}
              todos={dayTodos}
              isCurrentMonthDay={isCurrentMonthDay}
              isTodayDay={isTodayDay}
              isSelectedDay={isSelectedDay}
              holidays={state.holidays}
              currentUserId={state.currentUserId}
              onDateClick={handleDateClick}
              onDateDoubleClick={handleDateDoubleClick}
              onTodoClick={onTodoClick || (() => {})}
              onTodosUpdate={loadMonthTodos}
              setMonthTodos={setMonthTodos}
              cellDateKey={dateKey}
              onTodosInvalidate={onTodosInvalidate}
              onTodoUpdated={onTodoUpdated}
              onOptimisticToggle={onOptimisticToggle}
              getCurrentTodosForToggle={getCurrentTodosForToggle}
              currentTodosForToggle={currentTodosForToggle}
              isCompact={isCompactCell}
            />
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default CalendarDesktop;
